// src/components/SharedDashboard.jsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CollectionTransferModal from "./CollectionTransferModal";

// ------------------------------------
// Utils
// ------------------------------------
function getArtistDisplay(item) {
  if (!item) return "";

  return String(
    item.artist_display || // déjà normalisé
    item.artiste ||        // artworks (FR)
    item.artist ||         // collections / vues
    ""
  ).trim();
}

function getDisplayName(item, fallback = "") {
  if (!item) return fallback;

  const name = String(
    item.display_name || // déjà normalisé
    item.nom ||          // FR (collections / vues)
    item.title ||        // EN / artworks
    item.name ||         // legacy / fallback ultime
    ""
  ).trim();

  return name || fallback;
}

// ------------------------------------
// ✅ NOUVEAU : Modal de génération de rapport
// ------------------------------------
function ReportModal({ collection, onClose }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [artworks, setArtworks] = useState([]);
  const [artworksLoaded, setArtworksLoaded] = useState(false);
  const [reportTitle, setReportTitle] = useState(() =>
    t("sharedDashboard.report.defaultTitle", { collection: getDisplayName(collection, t("sharedDashboard.common.untitled")) })
  );
  const [options, setOptions] = useState({
    includeTitle: true,
    includeArtist: true,
    includeMedium: true,
    includeDimensions: true,
    includePrice: false,
    includeLocation: false,
    includeThumbnail: true,
    includeDate: true,
  });

  useEffect(() => {
    const fetchArtworks = async () => {
      setArtworksLoaded(false);
      try {
        const collId = collection.collection_id || collection.id;

        // Essai 1 — vue expert (bypass RLS pour les collections partagées)
        const { data: expertData, error: expertError } = await supabase
          .from("v_expert_artworks")
          .select("id, title, artist, medium, dimensions, value, estimated_value, location, images, image_url, collection_id")
          .eq("collection_id", collId);

        if (!expertError && expertData && expertData.length > 0) {
          setArtworks(expertData);
          return;
        }

        // Essai 2 — fallback sur artworks direct (collecteur propriétaire)
        const { data, error } = await supabase
          .from("artworks")
          .select("id, title, artist, medium, dimensions, value, estimated_value, location, images, image_url")
          .eq("collection_id", collId);
        if (error) throw error;
        setArtworks(data || []);
      } catch (e) {
        console.error("Erreur chargement œuvres:", e);
      } finally {
        setArtworksLoaded(true);
      }
    };
    fetchArtworks();
  }, [collection]);

  const toggle = (key) => setOptions((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleGenerate = async () => {
    if (!artworks.length) {
      alert(t("sharedDashboard.report.noArtworks"));
      return;
    }
    setLoading(true);
    try {
      const { createReportPDF } = await import("../lib/createReportPDF");

      const { data: { user } } = await supabase.auth.getUser();
      let advisor = null;
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("first_name, last_name, role, advisor_logo_url, advisor_title")
          .eq("id", user.id)
          .single();
        if (profile?.role === "expert") {
          advisor = {
            name: [profile.first_name, profile.last_name].filter(Boolean).join(" ") || null,
            title: profile.advisor_title || null,
            logoUrl: profile.advisor_logo_url || null,
          };
        }
      }

      const artworksForPDF = artworks.map((a) => {
        let thumbnailUrl = a.image_url || "";
        if (!thumbnailUrl && Array.isArray(a.images) && a.images.length) {
          const primary = a.images.find((x) => x?.isPrimary || x?.is_primary) || a.images[0];
          thumbnailUrl = typeof primary === "string" ? primary : primary?.url || "";
        }
        return {
          title: a.title || t("sharedDashboard.common.untitled"),
          artist: a.artist || "",
          medium: a.medium || "",
          dimensions: a.dimensions || "",
          price: a.value || a.estimated_value || null,
          location: a.location || "",
          thumbnailUrl,
        };
      });

      await createReportPDF({ title: reportTitle, artworks: artworksForPDF, options, advisor, output: "save" });
      onClose();
    } catch (e) {
      console.error("Erreur génération rapport:", e);
      alert(t("sharedDashboard.report.generateError"));
    } finally {
      setLoading(false);
    }
  };

  const optionLabels = {
    includeTitle: t("sharedDashboard.report.options.title"),
    includeArtist: t("sharedDashboard.report.options.artist"),
    includeMedium: t("sharedDashboard.report.options.medium"),
    includeDimensions: t("sharedDashboard.report.options.dimensions"),
    includePrice: t("sharedDashboard.report.options.price"),
    includeLocation: t("sharedDashboard.report.options.location"),
    includeThumbnail: t("sharedDashboard.report.options.thumbnail"),
    includeDate: t("sharedDashboard.report.options.date"),
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", borderRadius: 2, padding: 32, maxWidth: 520, width: "100%", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* En-tête */}
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 400, color: "#1a1a1a", margin: 0, marginBottom: 6 }}>
            {t("sharedDashboard.report.title")}
          </h2>
          <p style={{ fontFamily: "system-ui, sans-serif", fontSize: 13, color: "#6b6b6b", margin: 0 }}>
            {!artworksLoaded ? t("sharedDashboard.report.loadingArtworks") : t("sharedDashboard.report.artworkCount", { count: artworks.length })}
          </p>
        </div>

        {/* Titre du rapport */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", fontFamily: "system-ui, sans-serif", fontSize: 12, fontWeight: 600, color: "#1a1a1a", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("sharedDashboard.report.titleLabel")}
          </label>
          <input
            value={reportTitle}
            onChange={(e) => setReportTitle(e.target.value)}
            style={{ width: "100%", padding: "10px 12px", border: "1px solid #e5e3df", borderRadius: 2, fontFamily: "system-ui, sans-serif", fontSize: 14, color: "#1a1a1a", background: "#fafaf8", boxSizing: "border-box" }}
          />
        </div>

        {/* Options */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: "block", fontFamily: "system-ui, sans-serif", fontSize: 12, fontWeight: 600, color: "#1a1a1a", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {t("sharedDashboard.report.includedContent")}
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {Object.entries(optionLabels).map(([key, label]) => (
              <label
                key={key}
                style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "system-ui, sans-serif", fontSize: 13, cursor: "pointer", padding: "6px 10px", border: `1px solid ${options[key] ? "#1a1a1a" : "#e5e3df"}`, borderRadius: 2, background: options[key] ? "#1a1a1a" : "#fff", transition: "all 0.15s" }}
              >
                <input type="checkbox" checked={options[key]} onChange={() => toggle(key)} style={{ display: "none" }} />
                <span style={{ color: options[key] ? "#fff" : "#4a4a4a", fontSize: 12 }}>{options[key] ? "✓" : "○"}</span>
                <span style={{ color: options[key] ? "#fff" : "#4a4a4a" }}>{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Note co-marquage */}
        <div style={{ marginBottom: 24, padding: "10px 14px", background: "#fafaf8", border: "1px solid #e5e3df", borderRadius: 2 }}>
          <p style={{ fontFamily: "system-ui, sans-serif", fontSize: 12, color: "#6b6b6b", margin: 0, lineHeight: 1.5 }}>
            {t("sharedDashboard.report.coBrandingPrefix")} {" "}
            <strong>{t("sharedDashboard.report.profileIdentity")}</strong>.
          </p>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: "10px 20px", border: "1px solid #e5e3df", borderRadius: 2, background: "#fff", color: "#6b6b6b", fontFamily: "system-ui, sans-serif", fontSize: 14, cursor: "pointer" }}
          >
            {t("sharedDashboard.report.cancel")}
          </button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={loading || !artworksLoaded}
            style={{ padding: "10px 24px", border: "none", borderRadius: 2, background: (loading || !artworksLoaded) ? "#9b9b9b" : "#1a1a1a", color: "#fff", fontFamily: "system-ui, sans-serif", fontSize: 14, fontWeight: 600, cursor: (loading || !artworksLoaded) ? "not-allowed" : "pointer" }}
          >
            {loading ? t("sharedDashboard.report.generating") : t("sharedDashboard.report.downloadPdf")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SharedDashboard({ showClientBadge = true }) {
  const { user, profile } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  
  // ✅ Déterminer le préfixe de route selon le contexte
  const routePrefix = useMemo(() => {
    const pathname = location?.pathname || "";
    
    // Si on est déjà sur une route /expert/*, garder ce préfixe
    if (pathname.startsWith("/expert")) return "/expert";
    if (pathname.startsWith("/partner")) return "/partner";
    if (pathname.startsWith("/family-office")) return "/family-office";
    
    // Sinon, détecter selon le rôle du profile
    const role = profile?.role;
    if (role === "expert") return "/expert";
    if (role === "partner") return "/partner";
    if (role === "family_office") return "/family-office";
    
    // Fallback : route standard
    return "";
  }, [location?.pathname, profile?.role]);
  
  const [activeTab, setActiveTab] = useState("collections");
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [collections, setCollections] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");
  const [clients, setClients] = useState([]);
  const [clientById, setClientById] = useState({});  
  const [selectedClient, setSelectedClient] = useState("all");
  // Pré-sélection client (quand on arrive depuis "Voir" dans ClientManagement)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const clientFromUrl = params.get("client");
    if (clientFromUrl) setSelectedClient(clientFromUrl);
  }, [location.search]);


  // --- Chargement des données corrigé ---
  const loadData = async () => {
    if (!user?.id) {
      console.warn("Pas d'utilisateur connecté");
      return;
    }

    try {
      setLoading(true);
      setErr(null);

      console.log("🔍 Chargement des permissions pour expert:", user.id);

      // 1️⃣ RÉCUPÉRER LES PERMISSIONS (en tant qu'expert = grantee_id)
      const { data: permissions, error: permError } = await supabase
        .from('collection_permissions')
        .select('client_id, collection_id, can_view, can_edit')
        .eq('grantee_id', user.id)
        .eq('can_view', true);

      if (permError) {
        console.error("❌ Erreur permissions:", permError);
        throw permError;
      }

      console.log("✅ Permissions trouvées:", permissions?.length || 0, permissions);

      // Map permissions for fast lookup
      const permByCollectionId = new Map(
        (permissions || [])
          .filter((p) => !!p.collection_id)
          .map((p) => [p.collection_id, { can_view: !!p.can_view, can_edit: !!p.can_edit, client_id: p.client_id }])
      );

      // Client-wide permissions are represented by collection_id = null (access to all collections of a client)
      const permByClientId = new Map(
        (permissions || [])
          .filter((p) => !p.collection_id && !!p.client_id)
          .map((p) => [p.client_id, { can_view: !!p.can_view, can_edit: !!p.can_edit }])
      );

      // 2️⃣ FILTRER LES COLLECTION_IDS NON NULL
      const collectionIds = (permissions || [])
        .map(p => p.collection_id)
        .filter((id) => id !== null);

      const clientWideIds = (permissions || [])
        .filter((p) => !p.collection_id && p.client_id)
        .map((p) => p.client_id);

      console.log("📦 Collection IDs valides:", collectionIds.length, collectionIds);

      // 3️⃣ Collections : propres de l'expert + partagées via permissions
      const COLS_SELECT = `id, title, artist, description, price, image_url, client_id, created_at, updated_at`;

      const [ownCollResult, sharedCollResult] = await Promise.all([
        supabase.from("collections").select(COLS_SELECT).eq("client_id", user.id),
        (() => {
          if (!collectionIds.length && !clientWideIds.length) return Promise.resolve({ data: [], error: null });
          let q = supabase.from("collections").select(COLS_SELECT);
          if (collectionIds.length && clientWideIds.length) {
            q = q.or(`id.in.(${collectionIds.join(",")}),client_id.in.(${clientWideIds.join(",")})`);
          } else if (collectionIds.length) {
            q = q.in("id", collectionIds);
          } else {
            q = q.in("client_id", clientWideIds);
          }
          return q;
        })(),
      ]);

      if (ownCollResult.error) throw ownCollResult.error;
      if (sharedCollResult.error) { console.error("❌ Erreur collections partagées:", sharedCollResult.error); throw sharedCollResult.error; }

      const ownIds = new Set((ownCollResult.data || []).map((c) => c.id));

      const ownCollectionsData = (ownCollResult.data || []).map((c) => ({
        ...c,
        collection_id: c.id,
        nom: c.title,
        can_view: true,
        can_edit: true,
        grantee_id: user.id,
        isShared: false,
      }));

      const sharedCollectionsData = (sharedCollResult.data || [])
        .filter((c) => !ownIds.has(c.id))
        .map((c) => {
          const perm = permByCollectionId.get(c.id) || permByClientId.get(c.client_id);
          return {
            ...c,
            collection_id: c.id,
            nom: c.title,
            can_view: perm?.can_view || false,
            can_edit: perm?.can_edit || false,
            grantee_id: user.id,
            isShared: true,
          };
        });

      const collectionsData = [...ownCollectionsData, ...sharedCollectionsData];

      if (collectionsData.length === 0) {
        setCollections([]);
        setArtworks([]);
        setClients([]);
        setLoading(false);
        return;
      }

      console.log("✅ Collections chargées:", collectionsData?.length || 0, collectionsData);

      // 4️⃣ Œuvres : toutes les collections accessibles (propres + partagées)
      const allCollectionIds = collectionsData.map((c) => c.collection_id || c.id).filter(Boolean);
      let artQ = supabase
        .from("artworks")
        .select("id, title, artist, description, client_id, collection_id, created_at, updated_at, images, image_url, value, estimated_value");

      if (allCollectionIds.length) {
        artQ = artQ.in("collection_id", allCollectionIds);
      } else if (clientWideIds.length) {
        artQ = artQ.in("client_id", clientWideIds);
      } else {
        // rien à charger
        artQ = artQ.eq("client_id", user.id);
      }

      const { data: artworksData, error: artError } = await artQ;

      if (artError) {
        console.error("❌ Erreur artworks:", artError);
        throw artError;
      }

      console.log("✅ Œuvres chargées:", artworksData?.length || 0);
      // Permissions helper for artworks (collection-specific first, then client-wide)
      const getPermForArtwork = (art) =>
        permByCollectionId.get(art.collection_id) || permByClientId.get(art.client_id);

      // 5️⃣ Collections déjà enrichies via la vue (on normalise juste l'id)
      const enrichedCollections = (collectionsData || []).map((c) => ({
        ...c,
        id: c.collection_id || c.id,     // id stable = collection_id
        can_edit: !!c.can_edit,
        artist_display: getArtistDisplay(c),
        display_name: getDisplayName(c),
      }));
      const enrichedArtworks = artworksData.map(art => {
      const perm = getPermForArtwork(art);
        return {
          ...art,
          can_edit: perm?.can_edit || false,
     artist_display: getArtistDisplay(art),
     display_name: getDisplayName(art),
        };
      });

      setCollections(enrichedCollections);
      setArtworks(enrichedArtworks);
// 6️⃣ RÉCUPÉRER LES CLIENTS VIA LA VUE EXPERT (source de vérité)
let profsById = {};
const { data: expertClients, error: ecError } = await supabase
  .from("v_expert_clients")
  .select("client_id, first_name, last_name")
  .eq("expert_id", user.id);

if (ecError) {
  console.warn("⚠️ Erreur v_expert_clients (fallback IDs):", ecError);
} else if (Array.isArray(expertClients)) {
  profsById = Object.fromEntries(
    expertClients.map((c) => [
      c.client_id,
      {
        id: c.client_id,
        first_name: c.first_name,
        last_name: c.last_name,
      },
    ])
  );
  setClientById(profsById);
  console.log("✅ v_expert_clients chargés:", expertClients.length);
}

// 7️⃣ CONSTRUIRE LA LISTE DES CLIENTS POUR LE FILTRE (depuis la vue)
const clientMap = new Map();

Object.values(profsById).forEach((p) => {
  const id = p.id;
  const full = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  const name = full || t("sharedDashboard.common.clientNumber", { id: String(id).slice(0, 6) });
  clientMap.set(id, name);
});

// Si jamais la vue ne retourne personne (cas edge), on fallback sur items
if (clientMap.size === 0) {
  [...enrichedCollections, ...enrichedArtworks].forEach((item) => {
    const id = item.client_id;
    if (!id) return;
    if (!clientMap.has(id)) clientMap.set(id, t("sharedDashboard.common.clientNumber", { id: String(id).slice(0, 6) }));
  });
}

const clientsList = Array.from(clientMap, ([id, name]) => ({ id, name }))
  .sort((x, y) => x.name.localeCompare(y.name, i18n.resolvedLanguage || i18n.language || "fr", { sensitivity: "base" }));

setClients(clientsList);
console.log("✅ Liste clients:", clientsList.length, clientsList);

    } catch (e) {
      console.error("❌ Erreur loadData:", e);
      setErr(t("sharedDashboard.errors.load"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const normalizedIncludes = (text, q) =>
    String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .includes(
        String(q || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/\p{Diacritic}/gu, "")
      );

  const filterAndSort = (list) => {
    let arr = [...list];
    
    // Filtre par client
    if (selectedClient !== "all") {
      arr = arr.filter((x) => x.client_id === selectedClient);
    }
    
    // Filtre par recherche
    if (query.trim()) {
      arr = arr.filter((x) =>
        [getDisplayName(x), x.description, getArtistDisplay(x)].some((f) =>
          normalizedIncludes(f, query)
        )
      );
    }
    
    // Tri
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (sortKey === "nom" || sortKey === "name" || sortKey === "title") {
        const aVal = String(a.nom || a.name || a.title || "");
        const bVal = String(b.nom || b.name || b.title || "");
        return dir * aVal.localeCompare(bVal);
      }
      const av = new Date(a[sortKey] || a.updated_at || 0).getTime();
      const bv = new Date(b[sortKey] || b.updated_at || 0).getTime();
      return dir * (av - bv);
    });
    
    return arr;
  };

  // Collections appartenant à l'expert (hors partagées), au format attendu par la fenêtre de transfert
  const transferableCollections = useMemo(
    () => collections
      .filter((c) => !c.isShared)
      .map((c) => ({
        id: c.collection_id || c.id,
        title: c.title || c.nom || "",
        artworkCount: artworks.filter((a) => a.collection_id === (c.collection_id || c.id)).length,
      })),
    [collections, artworks]
  );

  const collectionsFiltered = useMemo(
    () => filterAndSort(collections),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [collections, query, sortKey, sortDir, selectedClient]
  );

  const artworksFiltered = useMemo(
    () => filterAndSort(artworks),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [artworks, query, sortKey, sortDir, selectedClient]
  );

  return (
    <section style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '48px 32px',
      fontFamily: 'Georgia, serif'
    }}>
      {/* En-tête avec onglets */}
      <div style={{ marginBottom: 40 }}>
        <div style={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid #e5e3df',
          marginBottom: 32
        }}>
          <button
            onClick={() => setActiveTab("collections")}
            style={{
              padding: '16px 24px',
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === "collections" ? '2px solid #1a1a1a' : '2px solid transparent',
              fontFamily: 'Georgia, serif',
              fontSize: 15,
              color: activeTab === "collections" ? '#1a1a1a' : '#6b6b6b',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: activeTab === "collections" ? 500 : 400
            }}
          >
            {t("sharedDashboard.tabs.collections", { count: collections.length })}
          </button>
          <button
            onClick={() => setActiveTab("artworks")}
            style={{
              padding: '16px 24px',
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === "artworks" ? '2px solid #1a1a1a' : '2px solid transparent',
              fontFamily: 'Georgia, serif',
              fontSize: 15,
              color: activeTab === "artworks" ? '#1a1a1a' : '#6b6b6b',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: activeTab === "artworks" ? 500 : 400
            }}
          >
            {t("sharedDashboard.tabs.artworks", { count: artworks.length })}
          </button>
          {/* Transférer ses propres collections vers un client (expert uniquement) */}
          {profile?.role === "expert" && transferableCollections.length > 0 && (
            <button
              onClick={() => setShowTransferModal(true)}
              title={t("round36.collectionsList.transfer.title")}
              style={{
                marginLeft: "auto", alignSelf: "center",
                fontSize: 12, padding: "6px 14px", borderRadius: 4, cursor: "pointer",
                border: "1px solid #c9a961", color: "#c9a961", background: "#fff",
                fontFamily: "system-ui", fontWeight: 600, whiteSpace: "nowrap",
              }}
            >
              {t("round36.collectionsList.transfer.button")} →
            </button>
          )}
        </div>

        {/* Barre de recherche et filtres */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto auto auto',
          gap: 12,
          marginBottom: 24
        }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("sharedDashboard.searchPlaceholder")}
            style={{
              border: '1px solid #e5e3df',
              borderRadius: 2,
              padding: '10px 16px',
              fontSize: 14,
              fontFamily: 'system-ui, sans-serif',
              outline: 'none'
            }}
          />
          
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            style={{
              border: '1px solid #e5e3df',
              borderRadius: 2,
              padding: '10px 16px',
              fontSize: 14,
              fontFamily: 'system-ui, sans-serif',
              minWidth: 200,
              outline: 'none'
            }}
          >
            <option value="all">{t("sharedDashboard.allClients")}</option>
            {clients.map((cl) => (
              <option key={cl.id} value={cl.id}>
                {cl.name}
              </option>
            ))}
          </select>

          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            style={{
              border: '1px solid #e5e3df',
              borderRadius: 2,
              padding: '10px 16px',
              fontSize: 14,
              fontFamily: 'system-ui, sans-serif',
              outline: 'none'
            }}
          >
            <option value="updated_at">{t("sharedDashboard.sort.recentlyUpdated")}</option>
            <option value="created_at">{t("sharedDashboard.sort.recentlyCreated")}</option>
            <option value="nom">{t("sharedDashboard.sort.name")}</option>
          </select>

          <button
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            title={sortDir === "asc" ? t("sharedDashboard.sort.ascending") : t("sharedDashboard.sort.descending")}
            style={{
              border: '1px solid #e5e3df',
              borderRadius: 2,
              padding: '10px 16px',
              fontSize: 14,
              background: '#fff',
              cursor: 'pointer',
              fontFamily: 'system-ui, sans-serif'
            }}
          >
            {sortDir === "asc" ? "↑" : "↓"}
          </button>

          <button
            onClick={loadData}
            title={t("sharedDashboard.reload")}
            style={{
              border: '1px solid #e5e3df',
              borderRadius: 2,
              padding: '10px 16px',
              fontSize: 14,
              background: '#fff',
              cursor: 'pointer',
              fontFamily: 'system-ui, sans-serif'
            }}
          >
            ⟳
          </button>
        </div>
      </div>

      {err && (
        <div style={{
          color: '#991b1b',
          fontSize: 14,
          marginBottom: 24,
          padding: 16,
          background: '#fef2f2',
          borderRadius: 2,
          border: '1px solid #fecaca'
        }}>
          ❌ {err}
        </div>
      )}
      
      {loading && (
        <p style={{
          color: '#9ca3af',
          fontSize: 14,
          textAlign: 'center',
          padding: 48
        }}>
          {t("sharedDashboard.loading")}
        </p>
      )}

      {showTransferModal && (
        <CollectionTransferModal
          ownCollections={transferableCollections}
          onClose={() => setShowTransferModal(false)}
        />
      )}

      {!loading && activeTab === "collections" && (
        <ItemGrid
          items={collectionsFiltered}
          type="collection"
          showClientBadge={showClientBadge}
          clientById={clientById}
          currentUserId={user?.id}
          routePrefix={routePrefix}
          locale={i18n.resolvedLanguage || i18n.language || "fr"}
        />
      )}
      
      {!loading && activeTab === "artworks" && (
        <ItemGrid 
          items={artworksFiltered} 
          type="artwork" 
          showClientBadge={showClientBadge} 
          clientById={clientById} 
	  routePrefix={routePrefix}
          locale={i18n.resolvedLanguage || i18n.language || "fr"}
        />
      )}
    </section>
  );
}

// --- Grille d'éléments avec design Artsy ---
function ItemGrid({ items, type, showClientBadge, clientById, currentUserId, routePrefix = "", locale = "fr" }) {
 const navigate = useNavigate();
 const { t } = useTranslation();
  // ✅ NOUVEAU : état pour la modal rapport
  const [reportCollection, setReportCollection] = useState(null);

  const clientLabelFromMap = (client_id, item) => {
    const p = clientById?.[client_id];
    if (p) {
      const full = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
      return full || p.company || t("sharedDashboard.common.clientNumber", { id: String(client_id).slice(0,6) });
    }
    return client_id ? t("sharedDashboard.common.clientNumber", { id: String(client_id).slice(0,6) }) : t("sharedDashboard.common.client");
  };

  if (!items?.length) {
    return (
      <div style={{
        textAlign: 'center',
        padding: 64,
        color: '#9ca3af',
        fontSize: 14,
        fontFamily: 'system-ui, sans-serif'
      }}>
        {t(type === "collection" ? "sharedDashboard.empty.collection" : "sharedDashboard.empty.artwork")}
      </div>
    );
  }

  return (
    <>
      {/* ✅ NOUVEAU : Modal rapport */}
      {reportCollection && (
        <ReportModal
          collection={reportCollection}
          onClose={() => setReportCollection(null)}
        />
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 32
      }}>
        {items.map((item) => {
          const isOwn = item.client_id === currentUserId;
          const clName = isOwn ? t("sharedDashboard.common.ownCollection") : clientLabelFromMap(item.client_id, item);
          const displayName = getDisplayName(item, t("sharedDashboard.common.untitled"));
          // ✅ Image (collection / artwork)
          const imgSrc = getCardImageSrc(item, type);
          const fallbackSrc =
            type === "collection"
              ? FALLBACK_COLLECTION
              : FALLBACK_ARTWORK;

          return (
            <div
              key={item.id}
              style={{
                background: '#fff',
                border: '1px solid #e5e3df',
                borderRadius: 2,
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#1a1a1a';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#e5e3df';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Image placeholder */}
              <div style={{
                position: 'relative',
                width: '100%',
                paddingTop: '75%',
                background: 'linear-gradient(135deg, #fafaf8 0%, #f5f5f4 100%)',
                overflow: 'hidden'
              }}>
                {/* ✅ Image réelle (si dispo) */}
                {!!imgSrc && (
                  <img
                    src={imgSrc}
                    alt={displayName}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = fallbackSrc;
                    }}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}

                <div style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg style={{
                    width: 64,
                    height: 64,
                    color: '#d4d4d4'
                  }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                
                {/* Badge "Partagé" — uniquement sur les collections clients */}
                {item.isShared && (
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: '#1a1a1a',
                    color: '#fff',
                    fontSize: 11,
                    padding: '4px 12px',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    fontFamily: 'system-ui, sans-serif'
                  }}>
                    {t("sharedDashboard.badges.shared")}
                  </div>
                )}

                {/* Badge mode (lecture / modification) — uniquement sur les collections clients */}
                {item.isShared && (
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    background: item.can_edit ? 'rgba(22,163,74,0.95)' : 'rgba(107,114,128,0.95)',
                    color: '#fff',
                    fontSize: 11,
                    padding: '4px 10px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    fontFamily: 'system-ui, sans-serif',
                    borderRadius: 2
                  }}>
                    {item.can_edit ? t("sharedDashboard.badges.edit") : t("sharedDashboard.badges.view")}
                  </div>
                )}

                {/* Badge CLIENT */}
                {showClientBadge && (
                  <div style={{
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    background: 'rgba(255,255,255,0.95)',
                    backdropFilter: 'blur(8px)',
                    color: '#1a1a1a',
                    fontSize: 11,
                    padding: '6px 12px',
                    fontWeight: 500,
                    border: '1px solid #e5e3df',
                    borderRadius: 2,
                    fontFamily: 'system-ui, sans-serif'
                  }}>
                    {clName}
                  </div>
                )}
              </div>

              {/* Contenu */}
              <div style={{ padding: 20 }}>
                <h3 style={{
                  fontSize: 16,
                  fontWeight: 400,
                  color: '#1a1a1a',
                  marginBottom: 8,
                  fontFamily: 'Georgia, serif',
                  letterSpacing: '-0.01em'
                }}>
                  {displayName}
                </h3>
                
                <p style={{
                  fontSize: 13,
                  color: '#6b6b6b',
                  marginBottom: 4,
                  fontFamily: 'system-ui, sans-serif'
                }}>
                  {t("sharedDashboard.labels.client")} <span style={{ color: '#1a1a1a', fontWeight: 500 }}>{clName}</span>
                </p>

                 {!!getArtistDisplay(item) && (
                  <p style={{
                    fontSize: 13,
                    color: '#6b6b6b',
                    marginBottom: 12,
                    fontFamily: 'system-ui, sans-serif'
                  }}>
                       {getArtistDisplay(item)}
                  </p>
                )}

                {item.description && (
                  <p style={{
                    fontSize: 13,
                    color: '#4a4a4a',
                    lineHeight: 1.6,
                    marginBottom: 12,
                    fontFamily: 'system-ui, sans-serif',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {item.description}
                  </p>
                )}

                {item.updated_at && (
                  <p style={{
                    fontSize: 12,
                    color: '#9ca3af',
                    marginBottom: 16,
                    fontFamily: 'system-ui, sans-serif'
                  }}>
                    {t("sharedDashboard.labels.modifiedOn", { date: formatDate(item.updated_at, locale) })}
                  </p>
                )}
                        {/*
                          IMPORTANT :
                          - pour les collections, la vue contient souvent collection_id (id réel de la collection)
                          - item.id peut être l'id de la vue / permission row
                        */}
                {/* ✅ MODIFIÉ : boutons côte à côte */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => {
                      const targetId = type === "collection" ? (item.collection_id || item.id) : item.id;
                      const path = `${routePrefix}/${type === "collection" ? "collections" : "artwork"}/${targetId}`;
                      navigate(path);
                    }}
                    style={{
                      display: 'inline-block',
                      fontSize: 13,
                      color: '#1a1a1a',
                      textDecoration: 'underline',
                      textUnderlineOffset: 4,
                      fontWeight: 500,
                      fontFamily: 'system-ui, sans-serif',
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      transition: 'color 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#6b6b6b'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#1a1a1a'}
                  >
                    {t(type === "collection" ? "sharedDashboard.actions.viewCollection" : "sharedDashboard.actions.viewArtwork")}
                  </button>

                  {/* ✅ NOUVEAU : bouton Rapport PDF — collections uniquement */}
                  {type === "collection" && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setReportCollection(item); }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 12,
                        color: '#c9a961',
                        border: '1px solid #c9a961',
                        borderRadius: 2,
                        padding: '5px 10px',
                        fontFamily: 'system-ui, sans-serif',
                        fontWeight: 600,
                        background: '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        letterSpacing: '0.03em',
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.background = '#c9a961'; e.currentTarget.style.color = '#fff'; }}
                      onMouseOut={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#c9a961'; }}
                    >
                      {t("sharedDashboard.actions.reportPdf")}
                    </button>
                  )}

                  {/* Mode présentation plein écran — collections uniquement */}
                  {type === "collection" && (
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/presentation/collection/${item.id}`); }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        fontSize: 12,
                        color: '#1a1a1a',
                        border: '1px solid #1a1a1a',
                        borderRadius: 2,
                        padding: '5px 10px',
                        fontFamily: 'system-ui, sans-serif',
                        fontWeight: 600,
                        background: '#fff',
                        cursor: 'pointer',
                        letterSpacing: '0.03em',
                      }}
                    >
                      {t("sharedDashboard.actions.present")}
                    </button>
                  )}
                </div>

              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function formatDate(d, locale = "fr") {
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ------------------------------------
// Image helpers (collections / artworks)
// ------------------------------------
const FALLBACK_COLLECTION =
  "/images/collections/Collection_Image_Not_Available.png";
const FALLBACK_ARTWORK =
  "/images/artworks/Vignette_oeuvre_Image_Not_Available.png";

function safeHttps(url) {
  if (typeof url !== "string") return "";
  return url.trim().replace(/^http:\/\//i, "https://");
}

function pickFromImagesField(images) {
  if (!images) return "";
  if (Array.isArray(images)) {
    const primary = images.find((x) => x?.isPrimary || x?.is_primary) || images[0];
    if (!primary) return "";
    if (typeof primary === "string") return primary;
    if (typeof primary === "object") return primary.url || primary.publicUrl || primary.public_url || primary.path || "";
    return "";
  }
  if (typeof images === "string") {
    try {
      const parsed = JSON.parse(images);
      return pickFromImagesField(parsed);
    } catch {
      return "";
    }
  }
  return "";
}

function getCardImageSrc(item, type) {
  if (!item) return "";
  if (type === "collection") return safeHttps(item.image_url || item.cover_image || item.image);
  // artwork
  return safeHttps(pickFromImagesField(item.images) || item.image_url);
}